import { LeafIcon } from '@/components/icons'
import PackVariantsIcon from '@/components/icons/PackVariantsIcon'
import WaterLevelSoilDarkIcon from '@/components/icons/WaterLevelSoilDarkIcon'
import Emitter from '@/features/config/pages/Emitter'
import Plants from '@/features/config/pages/Plants'
import Soil from '@/features/config/pages/Soil'
import useTranslation from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { Box, Stack, Typography } from '@mui/material'
import { ComponentType, FC, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { GuidedStepComponentProps } from '../guidedSteps'

interface ColumnConfig {
  key: string
  icon: FC<{ sx: { marginRight: string; color: string } }>
  title: string
  component: ComponentType<{
    titleSlot?: ReactNode
    interfaceId?: number | string
  }>
}

/**
 * SCRUM-1032 / SCRUM-1038 — Guided Plant / Soil / Emitter step.
 *
 * The guided-setup version of the Data Configuration Plant/Soil/Emitter view: drops the Areas &
 * Sub-Areas column (a separate guided step) and scopes every grid to the controller the wizard is
 * setting up (SCRUM-976), not the globally-selected interface. The delete confirmations these grids
 * raise are hosted centrally by SetUpGuided (ConfigUIProvider + ModalUI reused from ConfigContainer).
 */
const PlantSoilEmitterStep = ({ interfaceId }: GuidedStepComponentProps) => {
  const { t } = useTranslation()
  const isDark = useSelector(selectTheme) === mode_type.dark
  const accent = isDark ? '#fff' : '#004744'

  const columns: ColumnConfig[] = [
    { key: 'plant', icon: LeafIcon, title: t('menuDataConfigPlant'), component: Plants },
    { key: 'soil', icon: WaterLevelSoilDarkIcon, title: t('menuDataConfigSoil'), component: Soil },
    { key: 'emitter', icon: PackVariantsIcon, title: t('menuDataConfigEmitter'), component: Emitter },
  ]

  const renderColumn = ({
    key,
    icon: Icon,
    title,
    component: ActiveComponent,
  }: ColumnConfig): ReactNode => (
    <Box
      // Fixed, not the translated title: a language switch must not remount
      // the grids and drop what they have staged.
      key={key}
      sx={{
        // Equal-weight columns with a floor; if they don't fit the row scrolls
        // horizontally instead of squashing them.
        flex: '1 1 0',
        minWidth: '200px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ActiveComponent
        interfaceId={interfaceId}
        titleSlot={
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Icon sx={{ marginRight: '0px', color: accent }} />
            <Typography sx={{ color: 'text.main', fontWeight: 700, fontSize: '14px' }}>
              {title}
            </Typography>
          </Stack>
        }
      />
    </Box>
  )

  return (
    <Stack
      direction="row"
      sx={{
        height: '100%',
        overflowX: 'auto',
        p: '24px',
        gap: '24px',
        bgcolor: 'background.default',
      }}
    >
      {columns.map(renderColumn)}
    </Stack>
  )
}

export default PlantSoilEmitterStep
